import React from "react";
import { Badge } from "../../components/ui";

// Single source of truth for stock-state label/color so Core List and
// Insights render identical pills for the same state (explicit requirement).
export const STOCK_STATE_CONFIG = {
  owned: { label: "In stock", tone: "success" },
  exclusive: { label: "In consignment", tone: "purple" },
  no: { label: "Merchant owned", tone: "muted" },
  to_be_procured: { label: "To be procured", tone: "warning" },
  sold: { label: "Sold", tone: "default" },
  out: { label: "Out of stock", tone: "danger" },
  // A reference nobody has ever created a listing for. Previously collapsed
  // into `out`, which claimed we had stocked it and run out — the two need
  // different actions (create one vs. restock one), so they read differently.
  // "No listing" matches the wording the Listing column already uses for the
  // same row, so the two columns agree.
  never_listed: { label: "No listing", tone: "muted" },
};

// Shared derivation so Core List and Insights always agree on a reference's
// pill, whichever endpoint's raw fields they started from.
export const deriveStockState = ({ stock_state, hasSoldOnly, noListing }) => {
  if (hasSoldOnly) return "sold";
  // No listing row has EVER existed for this reference — distinct from a
  // listing that exists and is unavailable, which is what `out` means.
  if (noListing) return "never_listed";
  // A listing exists but carries no internal_product: it was created and then
  // switched off. That IS out of stock, and used to render a bare em dash.
  return stock_state || "out";
};

export const deriveStockStateFromRow = (row) => {
  const hasListing = row.most_recent_listing_id != null;
  const isSold = row.most_recent_listing_is_sold === true;
  return deriveStockState({
    stock_state: row.stock_state,
    hasSoldOnly: hasListing && isSold,
    noListing: !hasListing,
  });
};

/**
 * StockStatePill — renders one of the six stock states as an RC UI Badge.
 *
 * @param {"owned"|"exclusive"|"no"|"to_be_procured"|"sold"|"out"|"never_listed"} state
 */
const StockStatePill = ({ state }) => {
  const config = state ? STOCK_STATE_CONFIG[state] : null;
  if (!config) return <span className="core-list__muted">—</span>;
  return <Badge tone={config.tone}>{config.label}</Badge>;
};

export default StockStatePill;
